import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, ArrowRight, LayoutGrid, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventCard3D } from "@/components/events/EventCard3D";

interface ImportedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  ticket_status: string | null;
}

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

const parseEventDate = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;
  const arabicNums: { [key: string]: string } = { '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9' };
  const arabicMonths: { [key: string]: number } = {
    'يناير': 0, 'فبراير': 1, 'مارس': 2, 'أبريل': 3, 'إبريل': 3,
    'مايو': 4, 'يونيو': 5, 'يوليو': 6, 'أغسطس': 7,
    'سبتمبر': 8, 'أكتوبر': 9, 'نوفمبر': 10, 'ديسمبر': 11
  };
  
  let converted = dateStr;
  Object.entries(arabicNums).forEach(([ar, en]) => {
    converted = converted.replace(new RegExp(ar, 'g'), en);
  });
  
  const parts = converted.split(' ').filter(p => p.trim());
  if (parts.length >= 3) {
    const day = parseInt(parts[0]);
    const month = arabicMonths[parts[1]];
    const year = parseInt(parts[2]);
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      return new Date(year, month, day);
    }
  }
  
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const calculateCountdown = (targetDate: Date): CountdownTime => {
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  const isPast = diff <= 0;
  
  const absDiff = Math.abs(diff);
  const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((absDiff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, isPast };
};

const useCountdown = (date: string | null) => {
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);
  
  useEffect(() => {
    const eventDate = parseEventDate(date);
    if (!eventDate) return;
    
    const updateCountdown = () => setCountdown(calculateCountdown(eventDate));
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [date]);
  
  return countdown;
};

const EventCardWrapper = ({ event, isRTL }: { event: ImportedEvent; isRTL: boolean }) => {
  const countdown = useCountdown(event.date);
  return <EventCard3D event={event} isRTL={isRTL} countdown={countdown} />;
};

const Exhibitions = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const [importedEvents, setImportedEvents] = useState<ImportedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImportedEvents();
  }, []);

  const fetchImportedEvents = async () => {
    const { data, error } = await supabase
      .from("scraped_events")
      .select("*")
      .eq("is_published", true)
      .eq("category", "exhibitions")
      .order("scraped_at", { ascending: false });

    if (!error && data) {
      setImportedEvents(data);
    }
    setLoading(false);
  };

  return (
    <div className={`min-h-screen bg-gray-100 ${isRTL ? "rtl" : "ltr"}`}>
      <div className="container py-8">
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="flex items-center gap-2 hover:bg-gray-200"
          >
            {isRTL ? (
              <>
                العودة للرئيسية
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </>
            )}
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{
              background: 'linear-gradient(145deg, #ffffff, #e6e6e6)',
              boxShadow: '5px 5px 15px #d1d1d1, -5px -5px 15px #ffffff'
            }}>
              <LayoutGrid className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-gray-700">
              {isRTL ? "المعارض" : "Exhibitions"}
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : importedEvents.length === 0 ? (
          <div className="rounded-2xl p-8 text-center" style={{
            background: 'linear-gradient(145deg, #f0f0f0, #ffffff)',
            boxShadow: '8px 8px 20px #d1d1d1, -8px -8px 20px #ffffff'
          }}>
            <LayoutGrid className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              {isRTL ? "لا توجد معارض حالياً" : "No Exhibitions Available"}
            </h2>
            <p className="text-gray-400">
              {isRTL ? "سيتم إضافة المعارض قريباً" : "Exhibitions will be added soon"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {importedEvents.map((event) => (
              <EventCardWrapper key={event.id} event={event} isRTL={isRTL} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Exhibitions;
